import {Helmet} from "react-helmet-async";

import {StatusPageLayoutPreset, StatusPageTheme, type StatusPage} from "@/lib/types/statuspages.ts";

import {CurrentStatusOverview} from "../../components/currentstatus/CurrentStatusOverview.tsx";
import {HistoryOverview} from "../../components/history/HistoryOverview.tsx";
import {StatusPageFooter} from "../../components/StatusPageFooter.tsx";
import {StatusPageHeader} from "../../components/StatusPageHeader.tsx";
import {useFavicon} from "../../hooks/useFavicon.ts";
import {CurrentStatusTabType, StatusPageTab} from "../../types.ts";
import {getCrossTabReachability} from "../../utils/crossTabLinks.ts";
import type {ThemeProps} from "../types.ts";

import styles from "../../StatusPageConsumption.module.scss";

export const InspirePlusTheme = ({
  statusPage,
  slug,
  mode,
  pageId,
  isDark,
  onToggleDarkMode,
  activeTab,
  onTabChange,
  currentStatusTab,
  onStatusTabChange,
  historyTab,
  onHistoryTabChange,
  historyMounted,
  historyResult,
  allIncidents,
  statusDateRange,
  onRefresh,
  autoRefreshPageKey,
  componentStatuses,
  metrics,
  componentLinkTo,
}: ThemeProps) => {
  const {canViewActiveIncidents, canViewActiveMaintenance, canViewPastIncidents, canViewPastMaintenance} =
    getCrossTabReachability(statusPage, historyResult);

  // Tab visibility gates the content frames so a hidden tab never renders its content, even when it
  // is still the active tab (e.g. both tabs toggled off in the live preview).
  const statusVisible = statusPage.show_status_tab !== false;
  const historyVisible = !!statusPage.show_history_tab;
  useFavicon(statusPage.favicon_url);
  return (
    <div className={styles.pageWrapper} data-theme={isDark ? "dark" : "light"}>
      <Helmet>
        <title>{statusPage.name} — Status</title>
      </Helmet>

      <StatusPageHeader
        logoUrl={statusPage.logo_url}
        pageName={statusPage.name}
        pageDescription={statusPage.description}
        showStatusTab={statusPage.show_status_tab !== false}
        showHistoryTab={statusPage.show_history_tab}
        allowSubscriptions={statusPage.allow_subscriptions}
        allowSubscriptionsEmail={statusPage.allow_subscriptions_email}
        allowSubscriptionsSms={statusPage.allow_subscriptions_sms}
        allowSubscriptionsRss={statusPage.allow_subscriptions_rss}
        allowSubscriptionsSlack={statusPage.allow_subscriptions_slack}
        allowSubscriptionsWebhook={statusPage.allow_subscriptions_webhook}
        slackOAuthUrl={statusPage.slack_oauth_url ?? null}
        slug={slug}
        activeTab={activeTab}
        onTabChange={(tab) => onTabChange(tab)}
        onRefresh={onRefresh}
        autoRefreshPageKey={autoRefreshPageKey}
        isDarkMode={isDark}
        onToggleDarkMode={onToggleDarkMode}
        headerBgColor={statusPage.custom_header_bg_color_hex ?? null}
        headerTextColor={statusPage.custom_header_text_color_hex ?? null}
      />

      <main className={styles.bodyPlaceholder}>
        <div className={!statusVisible || activeTab !== StatusPageTab.STATUS ? styles.hidden : undefined}>
          <CurrentStatusOverview
            statusPage={statusPage as unknown as StatusPage}
            componentStatuses={componentStatuses}
            activeTab={currentStatusTab}
            onTabChange={(tab) => onStatusTabChange(tab)}
            onSwitchToHistory={(tab) => {
              if (!statusPage.show_history_tab) return;
              onTabChange(StatusPageTab.HISTORY);
              onHistoryTabChange(tab);
            }}
            canViewPastIncidents={canViewPastIncidents}
            canViewPastMaintenance={canViewPastMaintenance}
            metrics={metrics}
            mode={mode}
            slug={slug}
            pageId={pageId}
            componentLinkTo={componentLinkTo}
            layout={statusPage.layout_preset as StatusPageLayoutPreset}
            statusBarProps={
              statusPage.show_history_snake && statusDateRange
                ? {
                    incidents: allIncidents,
                    downtimeHistory: historyResult?.downtime_history ?? [],
                    statusMetadata: statusPage.component_status_metadata,
                    uptimeCalculationType: statusPage.uptime_calculation_type,
                    defaultStatusDateRange: statusPage.default_status_date_range || 7,
                    timezone: statusPage.used_timezone,
                    showHistorySnake: statusPage.show_history_snake,
                    globalIsOperational: statusPage.global_is_operational,
                    showOverallStatus: statusPage.show_overall_status,
                    hasMaintenanceState: statusPage.has_components_under_maintenance_state,
                    components: statusPage.components,
                    start: statusDateRange.start,
                    end: statusDateRange.end,
                    onViewIncidents:
                      statusPage.show_status_tab !== false
                        ? () => onStatusTabChange(CurrentStatusTabType.ACTIVE_INCIDENTS)
                        : undefined,
                  }
                : null
            }
          />
        </div>
        <div className={!historyVisible || activeTab !== StatusPageTab.HISTORY ? styles.hidden : undefined}>
          {historyVisible && historyMounted && (
            <HistoryOverview
              activeTab={historyTab}
              onTabChange={(tab) => onHistoryTabChange(tab)}
              mode={mode}
              variant={StatusPageTheme.INSPIRE_PLUS}
              layout={statusPage.layout_preset as StatusPageLayoutPreset}
              onSwitchToCurrentStatus={(tab) => {
                if (statusPage.show_status_tab === false) return;
                onTabChange(StatusPageTab.STATUS);
                onStatusTabChange(tab);
              }}
              canViewActiveIncidents={canViewActiveIncidents}
              canViewActiveMaintenance={canViewActiveMaintenance}
              componentLinkTo={componentLinkTo}
            />
          )}
        </div>
      </main>
      {statusPage.show_branding_footer && <StatusPageFooter />}
    </div>
  );
};
